import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Chip,
  Dialog,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setCommandPaletteOpen } from '@/app/uiSlice';
import { useAuth } from '@/features/auth/AuthProvider';

export interface Command {
  id: string;
  label: string;
  /** Grouping header, e.g. "Editor" or "Go to". */
  section: string;
  shortcut?: string;
  keywords?: string;
  run: () => void;
}

interface CommandRegistry {
  register: (commands: Command[]) => () => void;
}

const CommandContext = createContext<CommandRegistry | null>(null);

/**
 * Registers commands for as long as a component is mounted.
 *
 * Page-local commands (undo, publish, run the stress test) only make sense on
 * the page that owns them, and a global list would either go stale or need
 * every page's state hoisted to the root.
 */
export function useRegisterCommands(commands: Command[], deps: unknown[] = []) {
  const registry = use(CommandContext);

  useEffect(() => {
    if (!registry) return;
    return registry.register(commands);
    // The command closures are rebuilt on every render; the caller declares
    // what they actually depend on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registry, ...deps]);
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const open = useAppSelector((state) => state.ui.commandPaletteOpen);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const isOwner = user?.role === 'RESTAURANT_OWNER' || isAdmin;
  const [registrations, setRegistrations] = useState<Command[][]>([]);
  const [query, setQuery] = useState('');

  const register = useCallback((commands: Command[]) => {
    const entry = commands;
    setRegistrations((current) => [...current, entry]);
    return () => setRegistrations((current) => current.filter((item) => item !== entry));
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        dispatch(setCommandPaletteOpen(true));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dispatch]);

  const commands = useMemo(() => {
    const navigation: Command[] = isAdmin
      ? [
          {
            id: 'go-admin-overview',
            label: 'Go to overview',
            section: 'Go to',
            run: () => void navigate('/admin/overview'),
          },
          {
            id: 'go-admin-templates',
            label: 'Go to templates',
            section: 'Go to',
            keywords: 'constructor designs',
            run: () => void navigate('/admin'),
          },
          {
            id: 'go-admin-restaurants',
            label: 'Go to restaurants',
            section: 'Go to',
            run: () => void navigate('/admin/restaurants'),
          },
          {
            id: 'go-admin-users',
            label: 'Go to users',
            section: 'Go to',
            keywords: 'accounts people',
            run: () => void navigate('/admin/users'),
          },
          {
            id: 'go-admin-environment',
            label: 'Go to environment',
            section: 'Go to',
            keywords: 'settings config',
            run: () => void navigate('/admin/environment'),
          },
        ]
      : [
          {
            id: 'go-overview',
            label: 'Go to overview',
            section: 'Go to',
            run: () => void navigate('/app'),
          },
          {
            id: 'go-templates',
            label: 'Go to templates',
            section: 'Go to',
            keywords: 'marketplace gallery designs',
            run: () => void navigate('/app/templates'),
          },
          {
            id: 'go-menus',
            label: 'Go to menus',
            section: 'Go to',
            run: () => void navigate('/app/menus'),
          },
          {
            id: 'go-instances',
            label: 'Go to menu books',
            section: 'Go to',
            run: () => void navigate('/app/instances'),
          },
          {
            id: 'go-brand',
            label: 'Go to Brand Studio',
            section: 'Go to',
            run: () => void navigate('/app/brand'),
          },
          {
            id: 'go-analytics',
            label: 'Go to analytics',
            section: 'Go to',
            run: () => void navigate('/app/analytics'),
          },
          ...(isOwner
            ? [
                {
                  id: 'go-billing',
                  label: 'Go to plan',
                  section: 'Go to',
                  keywords: 'billing subscription',
                  run: () => void navigate('/app/billing'),
                },
                {
                  id: 'go-settings',
                  label: 'Go to settings',
                  section: 'Go to',
                  run: () => void navigate('/app/settings'),
                },
              ]
            : [
                {
                  id: 'go-settings',
                  label: 'Go to settings',
                  section: 'Go to',
                  run: () => void navigate('/app/settings'),
                },
              ]),
        ];
    return [...registrations.flat(), ...navigation];
  }, [registrations, navigate, isAdmin, isOwner]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return commands;
    return commands.filter((command) =>
      `${command.label} ${command.section} ${command.keywords ?? ''}`
        .toLowerCase()
        .includes(needle),
    );
  }, [commands, query]);

  const close = () => {
    dispatch(setCommandPaletteOpen(false));
    setQuery('');
  };

  const registry = useMemo<CommandRegistry>(() => ({ register }), [register]);

  return (
    <CommandContext value={registry}>
      {children}

      <Dialog
        open={open}
        onClose={close}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { position: 'fixed', top: 80, m: 0 } } }}
      >
        <Box sx={{ p: 1.5, pb: 0.5 }}>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Type a command…"
            autoFocus
            fullWidth
            onKeyDown={(event) => {
              if (event.key === 'Enter' && filtered[0]) {
                close();
                filtered[0].run();
              }
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <List dense sx={{ maxHeight: 400, overflowY: 'auto', pb: 1 }}>
          {filtered.map((command, index) => (
            <ListItemButton
              key={command.id}
              selected={index === 0 && query.length > 0}
              onClick={() => {
                close();
                command.run();
              }}
            >
              <ListItemText primary={command.label} />
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                {command.shortcut && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={command.shortcut}
                    sx={{ height: 18, fontSize: 10 }}
                  />
                )}
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {command.section}
                </Typography>
              </Stack>
            </ListItemButton>
          ))}
          {filtered.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, py: 3 }}>
              Nothing matches “{query}”.
            </Typography>
          )}
        </List>
      </Dialog>
    </CommandContext>
  );
}
